/** @jsxImportSource @emotion/react */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { lighten } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Buttons, TextFields } from '@zip/business-components';
import * as Icons from '@zip/icons';
import parse from 'autosuggest-highlight/parse';
import { theme } from 'global';
import { FC, useRef } from 'react';
import { AddressLookupInlineProps } from './AddressLookupInlineProps';
import * as styles from './AddressLookupInline.styles';
import ManualAddressFields from './ManualAddressFields';
import { useAddressLookup } from './useAddressLookup';

type AutocompletePrediction = google.maps.places.AutocompletePrediction;

const renderPredictionOption = (
  optionProps: React.HTMLAttributes<HTMLLIElement>,
  option: AutocompletePrediction
): JSX.Element => {
  if (!option.structured_formatting) {
    return <li {...optionProps} />;
  }

  const matches = option.structured_formatting.main_text_matched_substrings;
  const parts = parse(
    option.structured_formatting.main_text,
    matches.map((match) => [match.offset, match.offset + match.length])
  );

  return (
    <li {...optionProps}>
      <Grid container sx={{ flexGrow: 1 }} alignItems="center">
        <Grid>
          <Icons.MapPin
            css={{ color: theme.colors.neutral[300], marginRight: 8 }}
          />
        </Grid>
        <Grid size="grow">
          {parts.map((part, index) => (
            <span
              key={index}
              style={{ fontWeight: part.highlight ? 700 : 400 }}
            >
              {part.text}
            </span>
          ))}
          <Typography variant="body2" color="textSecondary">
            {option.structured_formatting.secondary_text}
          </Typography>
        </Grid>
      </Grid>
    </li>
  );
};

const AddressLookupInline: FC<AddressLookupInlineProps> = ({
  onBlur,
  label,
  value,
  error,
  helperText,
  handleChange,
  name,
  manualAddress,
  autoResolve = false,
}) => {
  const autocompleteId = `${name}-google-places-autocomplete`;
  const rootRef = useRef<HTMLDivElement>(null);

  const {
    autoCompleteValue,
    inputValue,
    options,
    selectedAddress,
    manualEntryOpen,
    handleAutoCompleteChange,
    handleInputChange,
    setIsManualEntryOpen,
    syncManualAddress,
  } = useAddressLookup({
    value,
    error,
    handleChange,
    manualAddress,
    autoResolve,
    hostElementRef: rootRef,
  });

  const manualEntryButton = (
    <Buttons.Text
      type="button"
      css={styles.link}
      onClick={(e) => setIsManualEntryOpen(e, true)}
    >
      enter address manually
    </Buttons.Text>
  );

  const addressLookupButton = (
    <Buttons.Text
      type="button"
      css={styles.link}
      onClick={(e) => setIsManualEntryOpen(e, false)}
    >
      Use address search
    </Buttons.Text>
  );

  if (manualEntryOpen) {
    return (
      <ManualAddressFields
        address={selectedAddress ?? manualAddress}
        showValidationErrors={error}
        addressLookupButton={addressLookupButton}
        onChange={syncManualAddress}
      />
    );
  }

  return (
    <div ref={rootRef}>
      <Autocomplete
        id={autocompleteId}
        getOptionLabel={(option): string =>
          typeof option === 'string' ? option : (option.description ?? '')
        }
        filterOptions={(x): AutocompletePrediction[] => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={autoCompleteValue}
        inputValue={inputValue ?? ''}
        forcePopupIcon={false}
        disableClearable
        freeSolo
        onChange={handleAutoCompleteChange}
        onInputChange={handleInputChange}
        renderInput={(params): JSX.Element => {
          const { InputLabelProps, InputProps, ...restParams } = params;
          return (
            <TextFields.Outlined
              {...restParams}
              name={name}
              label={label}
              css={styles.helperText}
              className="fs-mask"
              variant="outlined"
              fullWidth
              error={error}
              onBlur={(focus): void => {
                if (!focus.target.value) {
                  // `undefined` = user cleared the field;
                  // `null` is reserved for auto-resolve failure.
                  handleChange(null, undefined);
                }
                onBlur(focus);
              }}
              helperText={
                error && helperText ? (
                  <>
                    <ErrorOutlineIcon className="errorIcon" aria-hidden />
                    {helperText}&nbsp;or&nbsp;{manualEntryButton}
                  </>
                ) : (
                  <>Search address or&nbsp;{manualEntryButton}</>
                )
              }
              slotProps={{
                input: InputProps,
                inputLabel: InputLabelProps,
                formHelperText: { className: 'body4' },
              }}
            />
          );
        }}
        renderOption={renderPredictionOption}
      />
    </div>
  );
};

export default AddressLookupInline;
